import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import RefreshButton from "../../components/admin/RefreshButton";
import { TermsEditor } from "../../components/admin/TermsEditor";
import { useAdminToast } from "../../context/AdminToastContext";
import { parseApiError } from "../../lib/http/apiErrors";
import {
  addDiscountUsers,
  createDiscount,
  deactivateDiscount,
  fetchDiscounts,
  fetchRedemptions,
  previewDiscountTerms,
  setConfigValue,
  updateDiscount,
  type Discount,
  type DiscountInput,
  type Redemption,
} from "../../features/admin/billingApi";

/** Billing → Coupons & Discounts.
 *
 *  A discount is charged, not just shown: "first payment" lowers only the
 *  first charge; "every renewal" bills a discounted Cashfree plan for the
 *  life of the subscription (created when the discount is saved). If a
 *  customer qualifies for several, only the best one applies. Discounts are
 *  in rupees and apply to customers billed in INR. */

const TIERS: Record<string, string[]> = {
  seller: ["starter", "mid", "premium"],
  buyer: ["pro", "studio", "elite"],
};

const EMPTY: DiscountInput = {
  name: "",
  code: "",
  type: "percent",
  value: 10,
  duration: "first_payment",
  audience: { kind: "everyone", user_ids: [], roles: [], signed_up_after: null, new_subscribers_only: false },
  applies_to: { roles: [], tiers: [], cycles: [] },
  starts_at: null,
  ends_at: null,
  max_redemptions: null,
  per_user_limit: 1,
  active: true,
  listed: false,
  terms: null,
};

const input = "w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-indigo-500";
const labelCls = "mb-1 block text-xs font-medium text-gray-400";

function toggleIn<T>(list: T[], v: T): T[] {
  return list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
}

function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function describe(d: DiscountInput): string {
  const off = d.type === "percent" ? `${d.value}% off` : `₹${d.value.toLocaleString("en-IN")} off`;
  const when = d.duration === "first_payment" ? "first payment" : "every renewal";
  const who = d.audience.kind === "everyone" ? "everyone"
    : d.audience.kind === "users" ? `${d.audience.user_ids.length} user(s)` : "a user segment";
  const scope = [
    d.applies_to.roles.length ? d.applies_to.roles.join("/") : null,
    d.applies_to.tiers.length ? d.applies_to.tiers.join("/") : "all tiers",
    d.applies_to.cycles.length ? d.applies_to.cycles.join("/") : "monthly & yearly",
  ].filter(Boolean).join(" · ");
  return `${off} · ${when} · for ${who} · ${scope}`;
}

function DiscountForm({
  initial, onCancel, onSaved,
}: { initial: Discount | null; onCancel: () => void; onSaved: () => void }) {
  const { showError, showSuccess } = useAdminToast();
  const [f, setF] = useState<DiscountInput>(() => (initial ? { ...initial, code: initial.code ?? "" } : EMPTY));
  const [usersText, setUsersText] = useState("");
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof DiscountInput>(k: K, v: DiscountInput[K]) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.name.trim()) return showError("Give the discount a name.");
    if (f.value <= 0) return showError("The discount value must be above 0.");
    if (f.type === "percent" && f.value > 100) return showError("A percentage can't exceed 100.");
    if (f.listed && !(f.code ?? "").trim()) return showError("A listed discount needs a coupon code.");
    setBusy(true);
    try {
      const body: DiscountInput = {
        ...f,
        code: (f.code ?? "").trim().toUpperCase() || null,
        starts_at: f.starts_at ? new Date(f.starts_at).toISOString() : null,
        ends_at: f.ends_at ? new Date(f.ends_at).toISOString() : null,
      };
      const saved = initial ? await updateDiscount(initial.id, body) : await createDiscount(body);
      const refs = usersText.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
      if (refs.length) {
        const res = await addDiscountUsers(saved.id, refs);
        if (res.not_found.length) showError(`No user found for: ${res.not_found.join(", ")}`);
      }
      const planErrors = saved.plan_sync?.errors ?? [];
      if (planErrors.length) {
        showError(`Saved, but ${planErrors.length} discounted Cashfree plan(s) failed: ${planErrors[0].error}`);
      } else {
        showSuccess(initial ? "Discount updated." : "Discount created.");
      }
      onSaved();
    } catch (err) {
      showError(parseApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-6 space-y-5 rounded-xl border border-indigo-800/60 bg-gray-900 p-5">
      <h2 className="text-sm font-semibold text-white">{initial ? `Edit “${initial.name}”` : "New discount"}</h2>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className={labelCls}>Name <span className="text-gray-600">(shown on the coupon card when listed)</span></label>
          <input className={input} value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Diwali 2026 — 20% off" />
        </div>
        <div>
          <label className={labelCls}>Coupon code <span className="text-gray-600">(leave empty = applied automatically)</span></label>
          <input className={`${input} font-mono uppercase`} value={f.code ?? ""} onChange={(e) => set("code", e.target.value.toUpperCase())} placeholder="DIWALI20" />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <label className={labelCls}>Type</label>
          <select className={input} value={f.type} onChange={(e) => set("type", e.target.value as DiscountInput["type"])}>
            <option value="percent">Percent off</option>
            <option value="flat">Flat ₹ off</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>{f.type === "percent" ? "Percent (1–100)" : "Rupees off"}</label>
          <input className={input} inputMode="numeric" value={f.value}
            onChange={(e) => set("value", Number(e.target.value.replace(/\D/g, "")) || 0)} />
        </div>
        <div>
          <label className={labelCls}>Applies to</label>
          <select className={input} value={f.duration} onChange={(e) => set("duration", e.target.value as DiscountInput["duration"])}>
            <option value="first_payment">First payment only (then full price)</option>
            {/* Permanent discounts are off the menu; an existing one still shows. */}
            {f.duration === "every_renewal" && <option value="every_renewal">Every renewal (for life)</option>}
          </select>
        </div>
      </div>

      {/* Who */}
      <div>
        <label className={labelCls}>Who can use it</label>
        <div className="flex flex-wrap gap-2">
          {(["everyone", "users", "segment"] as const).map((k) => (
            <button key={k} type="button"
              onClick={() => set("audience", { ...f.audience, kind: k })}
              className={`rounded-lg px-3 py-1.5 text-sm ${f.audience.kind === k ? "bg-indigo-600 text-white" : "bg-gray-800 text-gray-400 hover:text-white"}`}>
              {k === "everyone" ? "Everyone" : k === "users" ? "Specific users" : "A group of users"}
            </button>
          ))}
        </div>
        {f.audience.kind === "users" && (
          <div className="mt-3">
            <textarea className={`${input} h-20 font-mono`} value={usersText} onChange={(e) => setUsersText(e.target.value)}
              placeholder="Emails or user IDs, separated by commas or new lines" />
            {f.audience.user_ids.length > 0 && (
              <p className="mt-1 text-xs text-gray-500">{f.audience.user_ids.length} user(s) already added.</p>
            )}
          </div>
        )}
        {f.audience.kind === "segment" && (
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-gray-300">
            {(["seller", "buyer"] as const).map((r) => (
              <label key={r} className="flex items-center gap-2">
                <input type="checkbox" className="accent-indigo-500" checked={f.audience.roles.includes(r)}
                  onChange={() => set("audience", { ...f.audience, roles: toggleIn(f.audience.roles, r) })} />
                {r === "seller" ? "Sellers" : "Buyers"}
              </label>
            ))}
            <label className="flex items-center gap-2">
              <input type="checkbox" className="accent-indigo-500" checked={f.audience.new_subscribers_only}
                onChange={(e) => set("audience", { ...f.audience, new_subscribers_only: e.target.checked })} />
              Never subscribed before
            </label>
            <label className="flex items-center gap-2">
              Signed up after
              <input type="date" className={`${input} w-auto`}
                value={f.audience.signed_up_after ? f.audience.signed_up_after.slice(0, 10) : ""}
                onChange={(e) => set("audience", { ...f.audience, signed_up_after: e.target.value ? new Date(e.target.value).toISOString() : null })} />
            </label>
          </div>
        )}
      </div>

      {/* Which plans */}
      <div>
        <label className={labelCls}>Which plans <span className="text-gray-600">(nothing ticked = all)</span></label>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-300">
          {(["seller", "buyer"] as const).map((r) => (
            <div key={r} className="flex flex-wrap items-center gap-3">
              <span className="text-xs uppercase text-gray-500">{r}</span>
              {TIERS[r].map((t) => (
                <label key={t} className="flex items-center gap-1.5">
                  <input type="checkbox" className="accent-indigo-500" checked={f.applies_to.tiers.includes(t)}
                    onChange={() => set("applies_to", {
                      ...f.applies_to,
                      tiers: toggleIn(f.applies_to.tiers, t),
                      roles: f.applies_to.roles.includes(r) ? f.applies_to.roles : [...f.applies_to.roles, r],
                    })} />
                  {t[0].toUpperCase() + t.slice(1)}
                </label>
              ))}
            </div>
          ))}
          <div className="flex items-center gap-3">
            <span className="text-xs uppercase text-gray-500">billing</span>
            {(["monthly", "yearly"] as const).map((c) => (
              <label key={c} className="flex items-center gap-1.5">
                <input type="checkbox" className="accent-indigo-500" checked={f.applies_to.cycles.includes(c)}
                  onChange={() => set("applies_to", { ...f.applies_to, cycles: toggleIn(f.applies_to.cycles, c) })} />
                {c === "monthly" ? "Monthly" : "Yearly"}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* When & limits */}
      <div className="grid gap-4 md:grid-cols-4">
        <div>
          <label className={labelCls}>Starts</label>
          <input type="datetime-local" className={input} value={toLocalInput(f.starts_at)} onChange={(e) => set("starts_at", e.target.value || null)} />
        </div>
        <div>
          <label className={labelCls}>Ends</label>
          <input type="datetime-local" className={input} value={toLocalInput(f.ends_at)} onChange={(e) => set("ends_at", e.target.value || null)} />
        </div>
        <div>
          <label className={labelCls}>Total uses <span className="text-gray-600">(empty = no limit)</span></label>
          <input className={input} inputMode="numeric" value={f.max_redemptions ?? ""}
            onChange={(e) => set("max_redemptions", e.target.value ? Number(e.target.value.replace(/\D/g, "")) || null : null)} />
        </div>
        <div>
          <label className={labelCls}>Uses per user <span className="text-gray-600">(empty = no limit)</span></label>
          <input className={input} inputMode="numeric" value={f.per_user_limit ?? ""}
            onChange={(e) => set("per_user_limit", e.target.value ? Number(e.target.value.replace(/\D/g, "")) || null : null)} />
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-300">
        <input type="checkbox" className="accent-indigo-500" checked={f.active} onChange={(e) => set("active", e.target.checked)} />
        Active
      </label>

      <div>
        <label className="flex items-center gap-2 text-sm text-gray-300">
          <input type="checkbox" className="accent-indigo-500" checked={!!f.listed} disabled={!(f.code ?? "").trim()} onChange={(e) => set("listed", e.target.checked)} />
          Show in the available coupons list
        </label>
        <p className="mt-1 text-xs text-gray-500">
          Needs a coupon code. Lets users tap Apply at subscription checkout instead of typing the code. Only users who qualify see it, with their discounted price. Leave off to keep the code private.
        </p>
      </div>

      <TermsEditor
        value={f.terms ?? ""}
        onChange={(v) => set("terms", v || null)}
        draft={f}
        loadPreview={() => previewDiscountTerms({ ...f, name: f.name?.trim() || "Discount" })}
        textareaCls={input}
        labelCls={labelCls}
      />

      <p className="rounded-lg bg-gray-800/60 px-3 py-2 text-xs text-gray-400">{describe(f)}</p>

      <div className="flex justify-end gap-3">
        <button onClick={onCancel} className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
        <button onClick={() => void save()} disabled={busy}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50">
          {busy ? "Saving…" : initial ? "Save changes" : "Create discount"}
        </button>
      </div>
    </div>
  );
}

function RedemptionList({ id }: { id: string }) {
  const [rows, setRows] = useState<Redemption[] | null>(null);
  useEffect(() => { void fetchRedemptions(id).then(setRows).catch(() => setRows([])); }, [id]);
  if (!rows) return <p className="px-5 py-3 text-xs text-gray-500">Loading…</p>;
  if (!rows.length) return <p className="px-5 py-3 text-xs text-gray-500">Not used yet.</p>;
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-gray-500">
          <th className="px-5 py-2 font-medium">User</th><th className="px-3 py-2 font-medium">Plan</th>
          <th className="px-3 py-2 font-medium">Charged</th><th className="px-3 py-2 font-medium">Status</th>
          <th className="px-3 py-2 font-medium">When</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-800 text-gray-300">
        {rows.map((r, i) => (
          <tr key={i}>
            <td className="px-5 py-2 font-mono">{r.user_id}</td>
            <td className="px-3 py-2">{r.plan_key} · {r.cycle}</td>
            <td className="px-3 py-2">
              ₹{(r.charged_amount_minor / 100).toLocaleString("en-IN")}{" "}
              <span className="text-gray-500 line-through">₹{(r.list_amount_minor / 100).toLocaleString("en-IN")}</span>
            </td>
            <td className="px-3 py-2">{r.status}</td>
            <td className="px-3 py-2">{new Date(r.created_at).toLocaleString("en-IN")}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function AdminCouponsPage() {
  const { showError, showSuccess } = useAdminToast();
  const [data, setData] = useState<{ enabled: boolean; discounts: Discount[] } | null>(null);
  const [editing, setEditing] = useState<Discount | "new" | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await fetchDiscounts());
    } catch (err) {
      showError(parseApiError(err).message);
    }
  }, [showError]);

  useEffect(() => { void load(); }, [load]);

  const setEnabled = async (on: boolean) => {
    try {
      await setConfigValue("DISCOUNTS_ENABLED", on ? 1 : 0);
      showSuccess(on ? "Discounts are live at checkout." : "Discounts are off — everyone pays list price.");
      await load();
    } catch (err) {
      showError(parseApiError(err).message);
    }
  };

  const deactivate = async (d: Discount) => {
    if (!window.confirm(`Deactivate “${d.name}”? New customers can no longer use it. Customers who already have it every renewal keep it.`)) return;
    try {
      await deactivateDiscount(d.id);
      showSuccess("Deactivated.");
      await load();
    } catch (err) {
      showError(parseApiError(err).message);
    }
  };

  const list = [...(data?.discounts ?? [])].sort((a, b) => Number(b.active) - Number(a.active));

  return (
    <div>
      <AdminPageHeader
        title="Coupons & Discounts"
        subtitle="Coupon codes and discounts for chosen users. Charged at checkout; the best single discount applies."
        actions={
          <>
            <RefreshButton onRefresh={load} />
            <button onClick={() => setEditing("new")}
              className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500">
              + New discount
            </button>
          </>
        }
      />

      <div className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-gray-800 bg-gray-900 p-4">
        <div>
          <p className="text-sm text-white">Discounts at checkout</p>
          <p className="text-xs text-gray-500">
            Off = nobody gets a discount and the coupon field is hidden. Discounts apply to customers billed in ₹.
          </p>
        </div>
        <button type="button" role="switch" aria-checked={!!data?.enabled} disabled={!data}
          onClick={() => void setEnabled(!data?.enabled)}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${data?.enabled ? "bg-indigo-600" : "bg-gray-700"}`}>
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${data?.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
        </button>
      </div>

      {editing && (
        <DiscountForm
          initial={editing === "new" ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      )}

      <div className="overflow-hidden rounded-xl border border-gray-800 bg-gray-900">
        {!list.length ? (
          <p className="p-6 text-sm text-gray-500">No discounts yet. Create one with “+ New discount”.</p>
        ) : (
          <div className="divide-y divide-gray-800">
            {list.map((d) => (
              <div key={d.id}>
                <div className="flex flex-col gap-3 px-5 py-4 md:flex-row md:items-center">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-white">{d.name}</span>
                      {d.code ? (
                        <span className="rounded bg-indigo-900/50 px-2 py-0.5 font-mono text-xs text-indigo-300">{d.code}</span>
                      ) : (
                        <span className="rounded bg-gray-800 px-2 py-0.5 text-xs text-gray-400">automatic</span>
                      )}
                      <span className={`rounded px-2 py-0.5 text-xs ${d.active ? "bg-emerald-900/40 text-emerald-300" : "bg-gray-800 text-gray-500"}`}>
                        {d.active ? "active" : "inactive"}
                      </span>
                      {d.listed && <span className="rounded bg-sky-900/40 px-2 py-0.5 text-xs text-sky-300">Listed</span>}
                    </div>
                    <p className="mt-1 text-xs text-gray-400">{describe(d)}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3 text-sm">
                    <span className="text-gray-400">
                      used <b className="text-white">{d.used ?? 0}</b>{d.max_redemptions ? ` / ${d.max_redemptions}` : ""}
                    </span>
                    <button onClick={() => setOpen(open === d.id ? null : d.id)} className="text-gray-400 hover:text-white">
                      {open === d.id ? "Hide uses" : "Uses"}
                    </button>
                    <button onClick={() => setEditing(d)} className="text-indigo-400 hover:text-indigo-300">Edit</button>
                    {d.active && (
                      <button onClick={() => void deactivate(d)} className="text-red-400 hover:text-red-300">Deactivate</button>
                    )}
                  </div>
                </div>
                {open === d.id && <div className="border-t border-gray-800 bg-gray-950/40"><RedemptionList id={d.id} /></div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
